import React from "react";
import { Tooltip } from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Renders the tooltip content.
 * tooltip can be:
 *   { formula?: string, text: string, subtext?: string }
 */
const TooltipContent = ({ tooltip }) => {
  if (!tooltip) return null;
  const { formula, text, subtext } = typeof tooltip === "string"
    ? { text: tooltip }
    : tooltip;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {formula && (
        <div
          style={{
            background: "#f3f0ff", // Very light purple background
            borderRadius: "6px",
            padding: "8px 12px",
            fontFamily: "'SF Mono', 'Fira Mono', 'Consolas', monospace",
            fontSize: "0.75rem",
            fontWeight: 500,
            color: "#8b5cf6", // Purple text
            letterSpacing: "0.01em",
            lineHeight: 1.5,
          }}
        >
          {formula}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {text && (
          <div
            style={{
              fontSize: "0.77rem",
              color: "#374151", // Slightly darker grey for main text
              lineHeight: 1.4,
              fontWeight: 400,
            }}
          >
            {text}
          </div>
        )}
        {subtext && (
          <div
            style={{
              fontSize: "0.73rem",
              color: "#6b7280", // Lighter grey for subtext
              fontStyle: "italic",
              lineHeight: 1.4,
              fontWeight: 400,
            }}
          >
            {subtext}
          </div>
        )}
      </div>
    </div>
  );
};

const TaskSummaryCards = ({ cards = [] }) => {
  if (!cards.length) return null;

  return (
    <div className="task-summary-cards">
      {cards.filter(c => !c.hidden).map((card) => (
        <div className="task-summary-card" key={card.key}>
          {/* Label row: label + optional MUI info tooltip */}
          <div className="task-summary-card-header">
            <span className="task-summary-card-label">{card.label}</span>
            {card.tooltip && (
              <Tooltip
                title={<TooltipContent tooltip={card.tooltip} />}
                placement="bottom-end"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#fff",
                      color: "#374151",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                      borderRadius: "8px",
                      maxWidth: 280,
                      px: 1.5,
                      py: 1.2,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#fff",
                      "&::before": { border: "1px solid #e5e7eb" },
                    },
                  },
                }}
              >
                <span className="task-summary-card-info">
                  <InfoOutlinedIcon sx={{ fontSize: "14px", strokeWidth: 1 }} />
                </span>
              </Tooltip>
            )}
          </div>

          <span
            className={`task-summary-card-value${card.valueTone ? ` tone-${card.valueTone}` : ""}`}
          >
            {card.value}
          </span>
          <span className={`task-summary-card-sub${card.tone ? ` tone-${card.tone}` : ""}`}>
            {card.subtitle}
          </span>
        </div>
      ))}
    </div>
  );
};

export default TaskSummaryCards;
